import React, { useEffect, useState } from "react";
import { Alert, Box, Grid, Snackbar, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  getProductionForecastGroup,
  getProductionForecastGroups,
} from "api/resources/productionForecasts";
import ProductionForecastChart from "./ProductionForecastChart";
import Select from "components/Select";
import { get15MinIntervalsForDate } from "helpers/timeIntervals";

interface PastForecastsChartProps {
  filters: any;
  showTitle?: boolean;
  showDatePicker?: boolean;
  date?: Dayjs | null;
  onDateChange?: (date: Dayjs | null) => void;
}

const PastForecastsChart = ({
  filters,
  showTitle = true,
  showDatePicker = true,
  date,
  onDateChange,
}: PastForecastsChartProps) => {
  const [chartData, setChartData] = useState(null);
  const [internalDate, setInternalDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(false);

  // Use external date if provided, otherwise use internal state
  const selectedDate = date !== undefined ? date : internalDate;
  const handleDateChange = (newDate: Dayjs | null) => {
    if (onDateChange) {
      onDateChange(newDate);
    } else {
      setInternalDate(newDate);
    }
  };
  const [selectableForecasts, setSelectableForecasts] = useState([]);
  const [selectedForecast, setSelectedForecast] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [periodsLength, setPeriodsLength] = useState<number>(null);

  useEffect(() => {
    if (selectedDate) {
      setChartData(null);

      getProductionForecastGroups(
        dayjs(selectedDate).format("YYYY-MM-DD"),
        dayjs(selectedDate).format("YYYY-MM-DD"),
      )
        .then((res) => {
          setSelectableForecasts(res.data);

          if (res.data.length === 0) {
            setSnackbarMessage("No forecasts for selected date");
            setSnackbarOpen(true);
          }
        })
        .catch(() => {
          setSnackbarMessage("Error fetching forecasts for selected date");
          setSnackbarOpen(true);
        });
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedForecast) {
      setChartData(null);
      setLoading(true);

      getProductionForecastGroup(selectedForecast, filters, true)
        .then((res) => {
          setLoading(false);
          setChartData(res.data);

          const periods = get15MinIntervalsForDate(
            dayjs(selectedDate).format("YYYY-MM-DD"),
          );
          setPeriodsLength(periods.length);
        })
        .catch(() => {
          setSnackbarMessage("Error fetching forecast data");
          setSnackbarOpen(true);
          setLoading(false);
        });
    }
  }, [selectedForecast, filters]);

  return (
    <Box sx={{ mt: showTitle ? "2rem" : 0, mb: "2rem" }}>
      {(showTitle || showDatePicker) && (
        <Grid container sx={{ mb: "1rem" }}>
          {showTitle && (
            <Grid item xs={12} display="flex" alignItems="center">
              <Typography component="p" fontSize={18} fontWeight={500}>
                Past forecasts
              </Typography>
            </Grid>
          )}
          {showDatePicker && (
            <Grid
              item
              xs={12}
              display="flex"
              alignItems="center"
              sx={{ mt: showTitle ? 2 : 0 }}
            >
              <DatePicker
                label="Forecast date"
                value={selectedDate}
                maxDate={dayjs().add(1, "day")}
                onChange={(newValue) => handleDateChange(newValue)}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
                sx={{ mr: 1 }}
              />
              {selectableForecasts.length > 0 && (
                <Select
                  label="Select forecast"
                  id="selectedForecast"
                  name="selectedForecast"
                  value={selectedForecast}
                  minWidth={200}
                  onChange={(e: any) => setSelectedForecast(e.target.value)}
                  options={
                    selectableForecasts.length > 0
                      ? selectableForecasts.map((forecast: any) => {
                          return {
                            label: dayjs(forecast.createdAt).format(
                              "DD.MM HH:mm",
                            ),
                            value: forecast.id,
                          };
                        })
                      : []
                  }
                />
              )}
            </Grid>
          )}
        </Grid>
      )}
      {!showDatePicker && selectableForecasts.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Select
            label="Select forecast"
            id="selectedForecast"
            name="selectedForecast"
            value={selectedForecast}
            minWidth={200}
            onChange={(e: any) => setSelectedForecast(e.target.value)}
            options={selectableForecasts.map((forecast: any) => ({
              label: dayjs(forecast.createdAt).format("DD.MM HH:mm"),
              value: forecast.id,
            }))}
          />
        </Box>
      )}
      {loading && <Typography component="p">Loading chart...</Typography>}
      {chartData && (
        <ProductionForecastChart
          totalValues={chartData.totalValues}
          xAxisLength={periodsLength}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          variant="filled"
          severity={
            snackbarMessage === "Previous day forecast fetched successfully"
              ? "success"
              : "error"
          }
          sx={{ mb: "0.4rem" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PastForecastsChart;
